import { Portal } from '@headlessui/react';
import {
  type KeyboardEvent,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { OtsThemeRoot } from '../theme/OtsTheme';
import {
  positionOtsFloatingPanel,
  type OtsFloatingPlacement,
} from './ots-floating-position';

export type OtsFloatingAnchor = HTMLElement | { x: number; y: number };
export type OtsFloatingPanelProps = {
  anchor: OtsFloatingAnchor;
  onClose: () => void;
  children: ReactNode;
  label: string;
  placement?: OtsFloatingPlacement;
  compact?: boolean;
  className?: string;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
};

/** Nonmodal portaled surface for GWT anchors, React buttons, and context-menu coordinates. */
const FloatingSurface = ({
  anchor,
  onClose,
  children,
  label,
  placement = 'bottom start',
  compact,
  className,
  onKeyDown,
}: OtsFloatingPanelProps) => {
  const panel = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const [position, setPosition] =
    useState<ReturnType<typeof positionOtsFloatingPanel>>();
  useLayoutEffect(() => {
    const element = panel.current;
    if (!element) return;
    const priorFocus =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const restore = anchor instanceof HTMLElement ? anchor : priorFocus;
    let frame = 0;
    const update = () => {
      if (anchor instanceof HTMLElement && !anchor.isConnected) {
        close.current();
        return;
      }
      const rect =
        anchor instanceof HTMLElement
          ? anchor.getBoundingClientRect()
          : { left: anchor.x, top: anchor.y, width: 0, height: 0 };
      const viewport = window.visualViewport;
      const next = positionOtsFloatingPanel(
        rect,
        { width: element.offsetWidth, height: element.scrollHeight + element.offsetHeight - element.clientHeight },
        {
          left: viewport?.offsetLeft ?? 0,
          top: viewport?.offsetTop ?? 0,
          width: viewport?.width ?? window.innerWidth,
          height: viewport?.height ?? window.innerHeight,
        },
        placement
      );
      setPosition((old) =>
        old &&
        Object.keys(next).every(
          (key) =>
            old[key as keyof typeof next] === next[key as keyof typeof next]
        )
          ? old
          : next
      );
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    const outside = (event: PointerEvent) => {
      const path = event.composedPath();
      if (
        !path.includes(element) &&
        !(anchor instanceof HTMLElement && path.includes(anchor))
      )
        close.current();
    };
    const keyboard = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close.current();
      }
      // Keep the browser's normal Tab movement; do not turn the menu into a modal trap.
      if (event.key === 'Tab') close.current();
    };
    update();
    element.focus({ preventScroll: true });
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('keydown', keyboard, true);
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    window.visualViewport?.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('scroll', schedule);
    const observer =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(schedule)
        : undefined;
    observer?.observe(element);
    // Content expansion changes scrollHeight even when the surface is already capped.
    const mutations = new MutationObserver(schedule);
    mutations.observe(element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden'],
    });
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      mutations.disconnect();
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('keydown', keyboard, true);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      window.visualViewport?.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('scroll', schedule);
      const active =
        element.getRootNode() instanceof ShadowRoot
          ? (element.getRootNode() as ShadowRoot).activeElement
          : document.activeElement;
      if (
        (!active || active === document.body || element.contains(active)) &&
        restore?.isConnected
      )
        restore.focus({ preventScroll: true });
    };
  }, [anchor, placement]);
  return (
    <OtsThemeRoot colorScheme="inherit">
      <div
        ref={panel}
        role="dialog"
        aria-label={label}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        data-ots-floating-panel
        data-density={compact ? 'compact' : 'default'}
        style={{
          position: 'fixed',
          zIndex: 1200,
          ...position,
          // Never force 'visible': it would show through a hidden ancestor, such as a WaterGo
          // bridge overlay that stays hidden until its shadow-root CSS loads.
          visibility: position ? undefined : 'hidden',
        }}
        className={[
          'w-80 overflow-y-auto overscroll-contain rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] p-1 text-[var(--ots-text)] shadow-xl outline-none',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {children}
      </div>
    </OtsThemeRoot>
  );
};

export const OtsFloatingPanel = (props: OtsFloatingPanelProps) => (
  <Portal>
    <FloatingSurface {...props} />
  </Portal>
);
